import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add,
  Search as SearchIcon,
  Refresh,
  FilterList,
  ReceiptLong,
  HourglassTop,
  CheckCircle,
  Payments,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { RouterLink } from '../../utils/linkNav';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { hrService, EmployeeListItem } from '../../services/hrService';

// ---------------------------------------------------------------------------
// Types -- mirror the raw snake_case rows returned by GET /expenses.
// ---------------------------------------------------------------------------

export type ExpenseStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'PAID' | 'CANCELLED';
export type PaymentMethod = 'CASH' | 'BANK' | 'CARD' | 'UPI' | 'PETTY_CASH';
export type PaidBy = 'COMPANY' | 'EMPLOYEE_REIMBURSEMENT';

export interface ExpenseRow {
  id: number;
  expense_number: string;
  expense_date: string;
  employee_id: number | string | null;
  category: string;
  description: string | null;
  amount: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  cost_center_id: number | string | null;
  department: string | null;
  payment_method: PaymentMethod | null;
  paid_by: PaidBy;
  status: ExpenseStatus;
  [key: string]: any;
}

export interface ExpenseSummary {
  byStatus: Record<string, { count: number; value: number }>;
  byCategory: Record<string, { count: number; value: number }>;
  totalCount: number;
  totalValue: number;
  pendingApprovalCount: number;
  pendingApprovalValue: number;
}

export const STATUS_OPTIONS: ExpenseStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'PAID', 'CANCELLED'];

export const STATUS_TONE: Record<ExpenseStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  PAID: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

export const PAID_BY_OPTIONS: PaidBy[] = ['COMPANY', 'EMPLOYEE_REIMBURSEMENT'];

export const COMMON_CATEGORIES = [
  'Travel',
  'Office & Admin',
  'Fuel',
  'Repairs & Maintenance',
  'Salary',
  'Finance Cost',
  'Depreciation',
  'Purchases',
];

const CATEGORY_TINT: Record<string, string> = {
  Travel: '#0284c7',
  'Office & Admin': '#7c3aed',
  Fuel: '#d97706',
  'Repairs & Maintenance': '#059669',
  Salary: '#dc2626',
  'Finance Cost': '#be123c',
  Depreciation: '#64748b',
  Purchases: '#0f766e',
};

const FALLBACK_TINTS = ['#475569', '#0891b2', '#9333ea', '#ca8a04', '#16a34a', '#e11d48'];

/** Deterministic tint for arbitrary free-text categories not in the known map. */
export const categoryTint = (category: string): string => {
  if (CATEGORY_TINT[category]) return CATEGORY_TINT[category];
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return FALLBACK_TINTS[hash % FALLBACK_TINTS.length];
};

export const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

export const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const ExpensePage: React.FC = () => {
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [employeeMap, setEmployeeMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [paidByFilter, setPaidByFilter] = useState<PaidBy | ''>('');
  const [search, setSearch] = useState('');

  // Employees are only used to resolve a display name for employee_id --
  // loaded once, independent of the list filters.
  useEffect(() => {
    hrService
      .getEmployees({ limit: 500 })
      .then(({ employees }) => {
        const map: Record<string, string> = {};
        (employees || []).forEach((e: EmployeeListItem) => {
          map[String(e.id)] = `${e.employee_code} — ${e.first_name}${e.last_name ? ` ${e.last_name}` : ''}`;
        });
        setEmployeeMap(map);
      })
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<{ expenses: ExpenseRow[]; count: number }>('/expenses', {
        params: {
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          paidBy: paidByFilter || undefined,
          search: search || undefined,
        },
      }),
      api.get<{ summary: ExpenseSummary }>('/expenses/summary'),
    ])
      .then(([expRes, summaryRes]) => {
        setExpenses(expRes.expenses);
        setSummary(summaryRes.summary);
      })
      .catch((err: any) => setError(err.message || 'Failed to load expenses'))
      .finally(() => setLoading(false));
  }, [statusFilter, categoryFilter, paidByFilter, search]);

  // Debounce the free-text search only -- other filters refetch immediately.
  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter, paidByFilter, search]);

  const approvedStat = summary?.byStatus?.APPROVED;
  const paidStat = summary?.byStatus?.PAID;

  const categoryOptions = Array.from(
    new Set([...COMMON_CATEGORIES, ...Object.keys(summary?.byCategory || {})])
  ).sort();

  const topCategories = Object.entries(summary?.byCategory || {})
    .sort((a, b) => b[1].value - a[1].value)
    .slice(0, 6);

  const hasFilters = !!(statusFilter || categoryFilter || paidByFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setPaidByFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Expenses"
        subtitle="Record, approve and settle company and employee-reimbursed expenses"
        actions={
          <>
            <IconButton onClick={fetchAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<Add />}
              size="small"
              component={RouterLink}
              to="/finance/expenses/new"
            >
              New Expense
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard
          title="Total Expenses"
          value={summary ? summary.totalCount : null}
          icon={<ReceiptLong />}
          color="#2563eb"
          footer={
            summary ? (
              <Typography variant="caption" color="text.secondary">
                {formatIndianNumber(summary.totalValue)} recorded
              </Typography>
            ) : undefined
          }
        />
        <StatCard
          title="Pending Approval"
          value={summary ? summary.pendingApprovalCount : null}
          icon={<HourglassTop />}
          color="#d97706"
          footer={
            <Typography variant="caption" color="text.secondary">
              {formatIndianNumber(summary?.pendingApprovalValue ?? 0)} awaiting a decision
            </Typography>
          }
        />
        <StatCard
          title="Approved"
          value={approvedStat ? approvedStat.count : summary ? 0 : null}
          icon={<CheckCircle />}
          color="#059669"
          footer={
            <Typography variant="caption" color="text.secondary">
              {formatIndianNumber(approvedStat?.value ?? 0)} ready to pay
            </Typography>
          }
        />
        <StatCard
          title="Paid"
          value={paidStat ? paidStat.count : summary ? 0 : null}
          icon={<Payments />}
          color="#7c3aed"
          footer={
            <Typography variant="caption" color="text.secondary">
              {formatIndianNumber(paidStat?.value ?? 0)} settled
            </Typography>
          }
        />
      </Box>

      {topCategories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
            By category:
          </Typography>
          {topCategories.map(([cat, stat]) => (
            <TypePill key={cat} label={`${cat} · ${formatIndianNumber(stat.value)}`} tint={categoryTint(cat)} />
          ))}
        </Box>
      )}

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Expense # or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ExpenseStatus | '')}
          sx={{ minWidth: 170 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categoryOptions.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Paid By"
          value={paidByFilter}
          onChange={(e) => setPaidByFilter(e.target.value as PaidBy | '')}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">Company &amp; Reimbursement</MenuItem>
          {PAID_BY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Expense', 'Date', 'Employee', 'Department', 'Category', 'Amount', 'Payment', 'Paid By', 'Status'].map(
                  (h, i) => (
                    <TableCell key={h} align={i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchAll} />
                  </TableCell>
                </TableRow>
              ) : expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No expenses match these filters"
                      hint="Clear the filters, or record a new expense."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((exp) => (
                  <TableRow
                    key={exp.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/finance/expenses/${exp.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell
                        code={exp.expense_number}
                        tint={categoryTint(exp.category)}
                        icon={<ReceiptLong fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell>{fmtDate(exp.expense_date)}</TableCell>
                    <TableCell>{exp.employee_name || employeeMap[String(exp.employee_id)] || '—'}</TableCell>
                    <TableCell>{exp.department_name || exp.department || '—'}</TableCell>
                    <TableCell>
                      {(exp.category_name || exp.category)
                        ? <TypePill label={exp.category_name || exp.category} tint={categoryTint(exp.category_name || exp.category)} />
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(exp.total_amount))}
                      </Typography>
                      {toNum(exp.tax_amount) > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatIndianNumber(toNum(exp.amount))} + {formatIndianNumber(toNum(exp.tax_amount))} tax
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{exp.payment_method ? exp.payment_method.replace(/_/g, ' ') : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={exp.paid_by === 'EMPLOYEE_REIMBURSEMENT' ? 'Reimbursement' : 'Company'}
                        color={exp.paid_by === 'EMPLOYEE_REIMBURSEMENT' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusDot label={exp.status.replace(/_/g, ' ')} tone={STATUS_TONE[exp.status]} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default ExpensePage;
